import IconButton from '@/components/base/button/icon-button';
import Arrow from '@/components/base/floating/arrow';
import type { UseFloatingReturn } from '@floating-ui/react';
import { XCircle } from '@phosphor-icons/react';
import { clsx } from 'clsx';
import {
  type CSSProperties,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type Ref,
  forwardRef,
} from 'react';

interface Props extends ComponentPropsWithoutRef<'div'> {
  floatingStyles: CSSProperties;
  className?: string;
  animationClassName?: string;
  transformOriginStyles: CSSProperties;
  variant: 'dark' | 'light';
  arrowRef: Ref<SVGSVGElement>;
  transitionRef: Ref<HTMLDivElement>;
  floatingClassName?: string;
  arrowClassName?: string;
  context: UseFloatingReturn['context'];
  Header?: ReactNode;
  showCloseButton?: boolean;
  closeModal?: () => void;
}

const FloatingContainer = forwardRef<HTMLDivElement, Props>(
  (
    {
      floatingStyles,
      className,
      animationClassName,
      transformOriginStyles,
      context,
      variant,
      arrowRef,
      transitionRef,
      children,
      Header,
      floatingClassName,
      arrowClassName,
      showCloseButton,
      closeModal,
      ...floatingProps
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={clsx('z-50', floatingClassName)}
      style={floatingStyles}
      {...floatingProps}
    >
      <div
        ref={transitionRef}
        className={animationClassName}
        style={transformOriginStyles}
      >
        <Arrow
          context={context}
          variant={variant}
          ref={arrowRef}
          className={arrowClassName}
        />
        <div
          className={clsx(
            className,
            'flex max-w-[450px] flex-col gap-xs s:gap-s rounded-m text-left text-body',
            showCloseButton && '!pr-xl',
            variant === 'dark'
              ? 'bg-contrast text-inverted'
              : 'border-3 border-decorative bg-main',
          )}
        >
          {Header && (
            <div className="border-decorative border-b-1">{Header}</div>
          )}
          {children}
          {showCloseButton && (
            <div
              className="absolute top-0 right-0 mt-xs mr-xs"
              onClick={closeModal}
            >
              <IconButton
                Icon={XCircle}
                className="!text-inverted hover:!ring-white rounded-s bg-strong outline-white hover:bg-strong hover:outline-1"
                iconVariant="light"
                classNameIcon="!text-white"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  ),
);

FloatingContainer.displayName = 'FloatingContainer';

export default FloatingContainer;
